import React, { useState, useEffect, Fragment, useMemo } from 'react'
import useTableFixedHeader from 'hooks/useTableFixedHeader'
import { superAxiosRequest } from 'axiosApi'
import { Link } from 'react-router-dom'
import ContentHeader from 'components/UI/ContentHeader/ContentHeader'
import THead from 'components/ContentBox/Global/ReportTable/THead'
import st from 'components/ContentBox/Global/ReportTable/Table.module.scss'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import { localisationMaster } from 'Localisation/Localisation'
import ExportExcelButton from 'components/Modules/Reports/ExportExcelButton/ExportExcelButton'
import { observer } from 'mobx-react'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalization from 'hooks/useLocalization'
import { timeConverter } from 'components/ContentBox/Online-schedule/components/functions'

// ===== ГРАНИЦЫ =====
const BORDER_SOFT = '1px solid #eef0f3'
const BORDER_GROUP = '1px solid #d1d5db'
const BORDER_TEACHER = '1px solid #9ca3af'
const BORDER_VERT = '1px solid #e5e7eb'

// ===== СТИЛИ ТАБЛИЦЫ (UI) =====
const tableStyles = {
  table: {
    borderSpacing: 0,
    width: '100%',
    background: '#fff',
    border: BORDER_VERT,
    borderRadius: '8px',
    overflow: 'hidden',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  th: {
    padding: '14px 16px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#4b5563',
    textAlign: 'left',
    background: '#f3f4f6',
    whiteSpace: 'nowrap',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
  },
  cell: {
    padding: '14px 16px',
    fontSize: '13px',
    color: '#1f2937',
    verticalAlign: 'middle',
    borderRight: BORDER_VERT,
  },
  cellCenter: {
    padding: '14px 16px',
    fontSize: '13px',
    color: '#1f2937',
    textAlign: 'center',
    verticalAlign: 'middle',
    fontWeight: 500,
    borderRight: BORDER_VERT,
  },
  teacher: {
    padding: '16px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#111827',
    verticalAlign: 'middle',
    borderRight: '1px solid #d1d5db',
    minWidth: '200px',
  },
  groupNumber: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#111827',
    lineHeight: 1.3,
  },
  link: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#111827',
    lineHeight: 1.3,
  },
  groupName: {
    fontSize: '11px',
    color: '#9ca3af',
    fontWeight: 400,
    marginTop: '3px',
    lineHeight: 1.3,
  },
  statusText: {
    padding: '14px 16px',
    fontSize: '13px',
    color: '#4b5563',
    verticalAlign: 'middle',
    borderRight: BORDER_VERT,
  },
  countText: {
    padding: '14px 16px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#111827',
    textAlign: 'center',
    verticalAlign: 'middle',
  },
}

// ===== ПОДСЧЁТ ИТОГОВ ПО ПРЕПОДАВАТЕЛЮ =====
const calculateTeacherTotals = (teacher) => {
  let totalLessons = 0
  let totalStudents = 0
  const statusMap = {}

  teacher.groups.forEach((group) => {
    totalLessons += Number(group.lessons) || 0
    totalStudents += Number(group.total_students) || 0

    group.statuses.forEach((status) => {
      if (!statusMap[status.id]) {
        statusMap[status.id] = {
          id: status.id,
          name: status.name,
          count: 0,
        }
      }
      statusMap[status.id].count += Number(status.count) || 0
    })
  })

  return {
    totalLessons,
    totalStudents,
    statuses: Object.values(statusMap),
  }
}

const DeletedStudentsReport = () => {
  const [groupCatalog, setGroupCatalog] = useState({})
  const [rawReportData, setRawReportData] = useState(null)
  const [filter, updateFilter] = useLocalStorageFilter()

  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore

  const {
    refWrapper,
    refSticky,
    refThead,
    data,
    setData,
    isLoading,
    setIsLoading,
  } = useTableFixedHeader()

  const dateAfter = new Date()
  dateAfter.setMonth(dateAfter.getMonth() - 1)
  const firstDayOfLastMonth = new Date(new Date(dateAfter).setDate(1))
  const lastDayOfLastMonth = new Date(new Date().setDate(0))

  const { translate } = useLocalization({
    section: 'reports',
    subsection: 'deleted_students',
    dict: {
      edit: 'Edit',
      copy: 'Copy',
      delete: 'Delete',
      save: 'Save',
      filter: 'Filter',
      company: 'Company',
      reset_all: 'Reset all',
      apply: 'Apply',
      search: 'Search',
      end_date: 'End date',
      start_date: 'Start date',
    },
  })

  const cfg = {
    cols: [
      {
        name:
          localisationMaster('teacher', 'reports/deleted-students') ||
          'Учитель',
      },
      {
        name:
          localisationMaster('group', 'reports/deleted-students') || 'Группа',
      },
      {
        name:
          localisationMaster('lessons_count', 'reports/deleted-students') ||
          'Кол-во уроков',
      },
      {
        name:
          localisationMaster('total_students', 'reports/deleted-students') ||
          'Всего студентов',
      },
      {
        name:
          localisationMaster('student_status', 'reports/deleted-students') ||
          'Статус студента',
      },
      {
        name:
          localisationMaster('count', 'reports/deleted-students') ||
          'Количество',
      },
    ],
  }

  const filterCnf = [
    {
      type: 'date_day_picker',
      defaultDate: {
        after: firstDayOfLastMonth,
        before: lastDayOfLastMonth,
      },
      nameState: ['date_from', 'date_until'],
      label: 'Период',
      isRequired: true,
    },
    {
      type: 'select-multi',
      label: 'Локация',
      placeholder: 'Выберите локацию',
      sort_key: 'name',
      nameState: 'location',
      values: groupCatalog.locations || [],
      is_multi: true,
      option_label_key: 'name',
      option_value_key: 'id',
    },
    {
      type: 'select-multi',
      label: 'Учитель',
      placeholder: 'Выберите учителя',
      nameState: 'teacher',
      values: groupCatalog.teachers || [],
      is_multi: true,
      is_search: true,
      option_label_key: 'name',
      option_value_key: 'id',
    },
    {
      type: 'select-multi',
      label: 'Статус студента',
      placeholder: 'Выберите статус',
      nameState: 'status',
      values: groupCatalog.statuses || [],
      is_multi: true,
      option_label_key: 'name',
      option_value_key: 'id',
    },
    {
      type: 'type_selector',
      nameState: 'online',
      label: 'Формат',
      values: [
        { id: false, name: 'Офлайн' },
        { id: true, name: 'Онлайн' },
      ],
    },
  ]

  // === ТРАНСФОРМАЦИЯ ДАННЫХ ===
  const transformedData = useMemo(() => {
    if (
      !rawReportData ||
      typeof rawReportData !== 'object' ||
      Array.isArray(rawReportData)
    ) {
      return []
    }

    const teachersMap = {}
    const statusesCatalog = groupCatalog.statuses || []

    Object.values(rawReportData).forEach((group) => {
      Object.values(group.teachers || {}).forEach((teacher) => {
        if (!teachersMap[teacher.id]) {
          teachersMap[teacher.id] = {
            id: teacher.id,
            name: teacher.name,
            groups: [],
          }
        }

        if (
          !teachersMap[teacher.id].groups.find((g) => g.id === group.group_id)
        ) {
          const statusArray = Object.entries(group.statuses || {})
            .map(([statusId, count]) => {
              const statusInfo = statusesCatalog.find(
                (s) => s.id === parseInt(statusId),
              )
              return {
                id: parseInt(statusId),
                name: statusInfo ? statusInfo.name : `Статус ${statusId}`,
                count: count,
              }
            })
            .filter((s) => s.count > 0)

          teachersMap[teacher.id].groups.push({
            id: group.group_id,
            number: group.group_number,
            name: group.group_name,
            lessons: teacher.lessons,
            total_students: group.student_count,
            statuses: statusArray,
          })
        }
      })
    })

    return Object.values(teachersMap)
  }, [rawReportData, groupCatalog.statuses])

  useEffect(() => {
    setData(transformedData)
  }, [transformedData, setData])

  // === ЭКСПОРТ В EXCEL ===
  const downloadExcel = (displayData) => {
    import('xlsx-js-style').then((XLSX) => {
      const headRow = cfg.cols.map((col) => col.name)
      const tableData = []
      const merges = []
      const totalRowFlags = [] // true для строк "Итого"
      let currentRow = 1

      displayData.forEach((teacher) => {
        const teacherGroups = teacher.groups || []
        const teacherRowSpan = teacherGroups.reduce(
          (acc, g) => acc + g.statuses.length,
          0,
        )
        const teacherRowStart = currentRow

        teacherGroups.forEach((group) => {
          const groupRowSpan = group.statuses.length
          const groupRowStart = currentRow

          group.statuses.forEach((status, statusIdx) => {
            const row = []

            if (group === teacherGroups[0] && statusIdx === 0) {
              row.push(teacher.name)
            } else {
              row.push(null)
            }

            if (statusIdx === 0) {
              const groupText = group.name
                ? `${group.number}\n${group.name}`
                : `${group.number}`
              row.push(groupText)
            } else {
              row.push(null)
            }

            if (statusIdx === 0) {
              row.push(group.lessons || 0)
            } else {
              row.push(null)
            }

            if (statusIdx === 0) {
              row.push(group.total_students || 0)
            } else {
              row.push(null)
            }

            row.push(status.name)
            row.push(status.count)

            tableData.push(row)
            totalRowFlags.push(false) // обычная строка
            currentRow++
          })

          if (groupRowSpan > 1) {
            merges.push({
              s: { r: groupRowStart, c: 1 },
              e: { r: groupRowStart + groupRowSpan - 1, c: 1 },
            })
            merges.push({
              s: { r: groupRowStart, c: 2 },
              e: { r: groupRowStart + groupRowSpan - 1, c: 2 },
            })
            merges.push({
              s: { r: groupRowStart, c: 3 },
              e: { r: groupRowStart + groupRowSpan - 1, c: 3 },
            })
          }
        })

        if (teacherRowSpan > 1) {
          merges.push({
            s: { r: teacherRowStart, c: 0 },
            e: { r: teacherRowStart + teacherRowSpan - 1, c: 0 },
          })
        }

        // === СТРОКА ИТОГО ПО ПРЕПОДАВАТЕЛЮ (Excel) ===
        const totals = calculateTeacherTotals(teacher)
        const totalsStatuses = totals.statuses
        const totalsRowStart = currentRow

        totalsStatuses.forEach((totalStatus, idx) => {
          const row = []
          if (idx === 0) {
            row.push(`Итого: ${teacher.name}`)
          } else {
            row.push(null)
          }
          row.push(null) // группа
          if (idx === 0) {
            row.push(totals.totalLessons)
            row.push(totals.totalStudents)
          } else {
            row.push(null)
            row.push(null)
          }
          row.push(totalStatus.name)
          row.push(totalStatus.count)

          tableData.push(row)
          totalRowFlags.push(true) // итоговая строка
          currentRow++
        })

        if (totalsStatuses.length > 1) {
          merges.push({
            s: { r: totalsRowStart, c: 0 },
            e: { r: totalsRowStart + totalsStatuses.length - 1, c: 0 },
          })
          merges.push({
            s: { r: totalsRowStart, c: 1 },
            e: { r: totalsRowStart + totalsStatuses.length - 1, c: 1 },
          })
          merges.push({
            s: { r: totalsRowStart, c: 2 },
            e: { r: totalsRowStart + totalsStatuses.length - 1, c: 2 },
          })
          merges.push({
            s: { r: totalsRowStart, c: 3 },
            e: { r: totalsRowStart + totalsStatuses.length - 1, c: 3 },
          })
        }
      })

      const ws = XLSX.utils.aoa_to_sheet([headRow, ...tableData])

      ws['!cols'] = [
        { wch: 28 },
        { wch: 32 },
        { wch: 14 },
        { wch: 16 },
        { wch: 26 },
        { wch: 14 },
      ]

      ws['!rows'] = [{ hpt: 36 }]

      // === Создаём объекты для ВСЕХ ячеек, включая пустые ===
      for (let R = 0; R <= currentRow; ++R) {
        for (let C = 0; C < cfg.cols.length; ++C) {
          const cellRef = XLSX.utils.encode_cell({ c: C, r: R })
          if (!ws[cellRef]) {
            ws[cellRef] = { t: 's', v: '' }
          }
        }
      }

      ws['!ref'] = XLSX.utils.encode_range({
        s: { c: 0, r: 0 },
        e: { c: cfg.cols.length - 1, r: currentRow },
      })

      const range = XLSX.utils.decode_range(ws['!ref'])

      const thinBorder = { style: 'thin', color: { rgb: 'D9D9D9' } }
      const borderStyle = {
        top: thinBorder,
        bottom: thinBorder,
        left: thinBorder,
        right: thinBorder,
      }

      const COLOR_WHITE = 'FFFFFF'
      const COLOR_STRIPE = 'F8F9FB'
      const COLOR_TOTAL = 'EEF2FF'

      for (let R = range.s.r; R <= range.e.r; ++R) {
        // dataRowIndex: 0 — шапка, 1..N — строки tableData
        const dataRowIndex = R - 1
        const isTotalRow = R > 0 && totalRowFlags[dataRowIndex] === true

        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = { c: C, r: R }
          const cellRef = XLSX.utils.encode_cell(cellAddress)

          let style = {
            border: borderStyle,
            alignment: {
              vertical: 'center',
              wrapText: true,
              horizontal: 'left',
              indent: 1,
            },
            font: { name: 'Arial', sz: 11, color: { rgb: '333333' } },
            fill: { fgColor: { rgb: COLOR_WHITE } },
          }

          // Шапка
          if (R === 0) {
            style.font = {
              name: 'Arial',
              sz: 11,
              bold: true,
              color: { rgb: '000000' },
            }
            style.alignment = {
              vertical: 'center',
              horizontal: 'center',
              wrapText: true,
            }
            style.fill = { fgColor: { rgb: 'FFFFFF' } }
            ws[cellRef].s = style
            continue
          }

          if (isTotalRow) {
            style.fill = { fgColor: { rgb: COLOR_TOTAL } }
            style.font = {
              name: 'Arial',
              sz: 11,
              bold: true,
              color: { rgb: '1E40AF' },
            }
          } else {
            const stripeIndex = R - 1
            if (stripeIndex % 2 === 1) {
              style.fill = { fgColor: { rgb: COLOR_STRIPE } }
            }
          }

          if (C === 2 || C === 3 || C === 5) {
            style.alignment.horizontal = 'center'
            style.alignment.indent = 0
          }

          if (C === 0 && !isTotalRow) {
            style.font.bold = true
            style.font.color = { rgb: '1F2937' }
          }

          if (C === 1 && !isTotalRow) {
            style.font.bold = true
            style.font.color = { rgb: '1F2937' }
          }

          if (C === 4 && !isTotalRow) {
            style.font.color = { rgb: '4B5563' }
          }

          ws[cellRef].s = style
        }
      }

      ws['!merges'] = merges

      let wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Отчет')
      XLSX.writeFile(wb, 'Отчет_по_удаленным_студентам.xlsx')
    })
  }

  // 1. Загрузка справочников при первой загрузке (всегда)
  useEffect(() => {
    const apiConf = {
      method: 'get',
      link: 'progress_report/deleted_students_report',
    }
    superAxiosRequest(apiConf).then((res) => {
      if (res.data) {
        const { locations, teachers, statuses } = res.data

        const teachersArray = teachers
          ? Object.entries(teachers).map(([id, name]) => ({
              id: Number(id),
              name: name,
            }))
          : []

        setGroupCatalog({
          locations: locations || [],
          teachers: teachersArray,
          statuses: statuses || [],
        })
      }
    })
  }, [])

  // 2. Загрузка данных отчета — только когда пользователь явно задал фильтр
  useEffect(() => {
    if (!filter?.date_from || !filter?.date_until) {
      setRawReportData(null)
      setData([])
      return
    }

    const params = {
      ...filter,
      date_from: timeConverter(filter.date_from, 'today'),
      date_until: timeConverter(filter.date_until, 'today'),
    }

    Object.keys(params).forEach((key) => {
      if (params[key] === undefined || params[key] === '') delete params[key]
    })

    setIsLoading(true)
    const apiConf = {
      method: 'get',
      link: 'progress_report/deleted_students_report',
      params: params,
    }

    superAxiosRequest(apiConf)
      .then((res) => {
        if (res.data && !res.data.locations && typeof res.data === 'object') {
          setRawReportData(res.data)
        } else {
          setRawReportData(null)
          setData([])
        }
        setIsLoading(false)
      })
      .catch(() => {
        setRawReportData(null)
        setData([])
        setIsLoading(false)
      })
  }, [filter])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  const isFilterEmpty = !filterTextValues.length
  const hasDateFilter = !!filter?.date_from && !!filter?.date_until

  return (
    <>
      <ContentHeader title='Отчет по удаленным студентам' />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          margin: '10px',
        }}
      >
        <SearchBooks
          onSearch={(text) => {
            updateFilter({ ...filter, search: text })
            changeTextValue('search', {
              key: 'search',
              label: 'Search',
              value: text,
            })
          }}
          onClear={() => {
            const { search, ...obj } = filter
            updateFilter(obj)
            setTextValues(
              filterTextValues.filter((field) => field.key !== 'search'),
            )
          }}
          translate={translate}
          placeholder='Search...'
          value={filter?.search || ''}
          isFilterEmpty={isFilterEmpty}
        />

        {!!filterTextValues.length && (
          <FilterTextValues
            onDelete={(key) => {
              const { [key]: _, ...obj } = filter
              updateFilter(obj)
            }}
          />
        )}

        {isOpenFilter && (
          <AsideFilter
            translate={translate}
            getFilterParams={updateFilter}
            config={filterCnf}
            filterValue={filter}
            onDropFilter={() => {
              updateFilter({})
              closeFilter()
            }}
          />
        )}
      </div>

      {isLoading ? (
        <Loader />
      ) : !hasDateFilter ? (
        <h1 className={st.noData}>
          Выберите период в фильтре и нажмите «Применить»
        </h1>
      ) : data && data.length > 0 ? (
        <>
          <div style={{ padding: '10px' }}>
            <ExportExcelButton data={data} downloadExcel={downloadExcel} />
          </div>
          <div
            className={st.tableWrapper}
            ref={refWrapper}
            style={{ margin: '10px' }}
          >
            <div ref={refSticky} className={st.sticky}>
              <table style={tableStyles.table} className='report-table'>
                <THead cols={cfg.cols} />
              </table>
            </div>
            <table style={tableStyles.table} className='report-table'>
              <THead cols={cfg.cols} ref={refThead} />
              <tbody>
                {data.map((teacher, teacherIdx) => {
                  const teacherRowSpan = teacher.groups.reduce(
                    (acc, g) => acc + g.statuses.length,
                    0,
                  )
                  const isLastTeacher = teacherIdx === data.length - 1

                  const totals = calculateTeacherTotals(teacher)
                  const totalsStatuses = totals.statuses
                  const totalsRowSpan = totalsStatuses.length

                  return (
                    <Fragment key={teacher.id}>
                      {teacher.groups.map((group, groupIdx) => {
                        const groupRowSpan = group.statuses.length
                        const isLastGroup =
                          groupIdx === teacher.groups.length - 1

                        const groupBg =
                          groupIdx % 2 === 0 ? '#ffffff' : '#f8f9fb'

                        return (
                          <Fragment key={group.id}>
                            {group.statuses.map((status, statusIdx) => {
                              const isLastStatusInGroup =
                                statusIdx === groupRowSpan - 1

                              let statusBottom = BORDER_SOFT
                              if (isLastStatusInGroup) {
                                statusBottom = BORDER_GROUP
                              }

                              return (
                                <tr
                                  key={`${group.id}-${status.id}`}
                                  className='report-row'
                                >
                                  {groupIdx === 0 && statusIdx === 0 ? (
                                    <td
                                      rowSpan={teacherRowSpan}
                                      style={{
                                        ...tableStyles.teacher,
                                        background: '#ffffff',
                                        borderBottom: BORDER_GROUP,
                                      }}
                                    >
                                      {teacher.name}
                                    </td>
                                  ) : null}

                                  {statusIdx === 0 ? (
                                    <td
                                      rowSpan={groupRowSpan}
                                      style={{
                                        ...tableStyles.cell,
                                        background: groupBg,
                                        borderBottom: BORDER_GROUP,
                                      }}
                                    >
                                      <div style={tableStyles.groupNumber}>
                                        <Link
                                          target='_blank'
                                          to={'/groups/group/' + group.id}
                                          style={tableStyles.link}
                                        >
                                          {group.number}
                                        </Link>
                                      </div>
                                      {group.name && (
                                        <div style={tableStyles.groupName}>
                                          {group.name}
                                        </div>
                                      )}
                                    </td>
                                  ) : null}

                                  {statusIdx === 0 ? (
                                    <td
                                      rowSpan={groupRowSpan}
                                      style={{
                                        ...tableStyles.cellCenter,
                                        background: groupBg,
                                        borderBottom: BORDER_GROUP,
                                      }}
                                    >
                                      {group.lessons}
                                    </td>
                                  ) : null}

                                  {statusIdx === 0 ? (
                                    <td
                                      rowSpan={groupRowSpan}
                                      style={{
                                        ...tableStyles.cellCenter,
                                        background: groupBg,
                                        borderBottom: BORDER_GROUP,
                                      }}
                                    >
                                      {group.total_students}
                                    </td>
                                  ) : null}

                                  <td
                                    style={{
                                      ...tableStyles.statusText,
                                      background: groupBg,
                                      borderBottom: statusBottom,
                                    }}
                                  >
                                    {status.name}
                                  </td>

                                  <td
                                    style={{
                                      ...tableStyles.countText,
                                      background: groupBg,
                                      borderBottom: statusBottom,
                                    }}
                                  >
                                    {status.count}
                                  </td>
                                </tr>
                              )
                            })}
                          </Fragment>
                        )
                      })}

                      {/* ===== СТРОКА ИТОГО ПО ПРЕПОДАВАТЕЛЮ ===== */}
                      {totalsStatuses.map((totalStatus, totalIdx) => {
                        const isFirstTotalRow = totalIdx === 0
                        const isLastTotalRow = totalIdx === totalsRowSpan - 1

                        const rowBorderBottom = isLastTeacher
                          ? isLastTotalRow
                            ? 'none'
                            : BORDER_SOFT
                          : isLastTotalRow
                            ? BORDER_TEACHER
                            : BORDER_SOFT

                        return (
                          <tr
                            key={`total-${teacher.id}-${totalStatus.id}`}
                            style={{ background: '#eef2ff' }}
                          >
                            {isFirstTotalRow ? (
                              <>
                                <td
                                  colSpan={2}
                                  rowSpan={totalsRowSpan}
                                  style={{
                                    ...tableStyles.teacher,
                                    background: '#eef2ff',
                                    fontWeight: 700,
                                    color: '#1e40af',
                                    borderBottom: rowBorderBottom,
                                  }}
                                >
                                  Итого
                                </td>
                                <td
                                  rowSpan={totalsRowSpan}
                                  style={{
                                    ...tableStyles.cellCenter,
                                    background: '#eef2ff',
                                    fontWeight: 700,
                                    color: '#1e40af',
                                    borderBottom: rowBorderBottom,
                                  }}
                                >
                                  {totals.totalLessons}
                                </td>
                                <td
                                  rowSpan={totalsRowSpan}
                                  style={{
                                    ...tableStyles.cellCenter,
                                    background: '#eef2ff',
                                    fontWeight: 700,
                                    color: '#1e40af',
                                    borderBottom: rowBorderBottom,
                                  }}
                                >
                                  {totals.totalStudents}
                                </td>
                              </>
                            ) : null}

                            <td
                              style={{
                                ...tableStyles.statusText,
                                background: '#eef2ff',
                                fontWeight: 700,
                                color: '#1e40af',
                                borderBottom: rowBorderBottom,
                              }}
                            >
                              {totalStatus.name}
                            </td>

                            <td
                              style={{
                                ...tableStyles.countText,
                                background: '#eef2ff',
                                fontWeight: 700,
                                color: '#1e40af',
                                borderBottom: rowBorderBottom,
                              }}
                            >
                              {totalStatus.count}
                            </td>
                          </tr>
                        )
                      })}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <h1 className={st.noData}>Нет данных для отображения!</h1>
      )}
    </>
  )
}

export default observer(DeletedStudentsReport)
